import {
	GameState,
	gameStateArrayFields,
	gameStateSimpleFields,
} from '@lc/interface/gameState/types';
import {
	playerArrayFields,
	playerSimpleFields,
} from '@lc/interface/player/types';

// deep diff pizza
// for each key, if not an array, compare values with ===
// if array, push name to list for next round of testing
// for each array in list, if not playerStateArray, order the array and compare
// if length !== same or different values, capture difference
// finally playerStateArray
// recursively run the same check for players with matching id's, but record the player id
// i.e. run check, then add the id to the returned forward & reverse

function getMatchingPlayerById(id: number, a: any) {
	for (let j = 0; j < a.playerStateArray.length; j++) {
		if (a.playerStateArray[j].id === id) {
			return a.playerStateArray[j];
		}
	}

	return null;
}

export function getTwoWayDiffCustom(b: any, a: any) {
	const reverse: any = {};
	const forward: any = {};

	function compareSimpleTypes(
		b: any,
		a: any,
		reverse: any,
		forward: any,
		simpleFields: string[]
	) {
		// Compare simple types
		for (const field of simpleFields) {
			if (b[field] !== a[field]) {
				reverse[field] = b[field];
				forward[field] = a[field];
			}
		}
	}

	function arraysAreEqual(a1: any[], a2: any[]): boolean {
		if (a1.length !== a2.length) return false;

		for (let i = 0; i < a1.length; i++) {
			if (Array.isArray(a1[i]) && Array.isArray(a2[i])) {
				if (!arraysAreEqual(a1[i], a2[i])) return false;
			} else if (a1[i] !== a2[i]) {
				return false;
			}
		}
		return true;
	}

	function compareArrays(
		b: any,
		a: any,
		reverse: any,
		forward: any,
		arrayFields: string[]
	) {
		for (const field of arrayFields) {
			if (!arraysAreEqual(b[field], a[field])) {
				reverse[field] = b[field];
				forward[field] = a[field];
			}
		}
	}

	function isEmptyObject(obj: any) {
		// Check if the input is actually an object and not null or undefined
		if (obj === null) {
			return false; // Or throw an error, depending on desired behavior for non-objects
		}
		return Object.keys(obj).length === 0;
	}

	// Handle nested objects like worldEnergy
	if (JSON.stringify(b.worldEnergy) !== JSON.stringify(a.worldEnergy)) {
		reverse.worldEnergy = b.worldEnergy;
		forward.worldEnergy = a.worldEnergy;
	}

	compareSimpleTypes(b, a, reverse, forward, gameStateSimpleFields);
	compareArrays(b, a, reverse, forward, gameStateArrayFields);

	reverse.playerStateArray = [];
	forward.playerStateArray = [];

	for (let i = 0; i < b.playerStateArray.length; i++) {
		const reverse_player = {};
		const forward_player = {};

		const playerB = b.playerStateArray[i];
		const playerA = getMatchingPlayerById(playerB.id, a);

		if (playerA == null) {
			console.log(
				'Ruh roh raggy, error in getTwoWayDiffCustom. Cant find player'
			);
			return { reverse, forward };
		}

		compareSimpleTypes(
			playerB,
			playerA,
			reverse_player,
			forward_player,
			playerSimpleFields
		);
		compareArrays(
			playerB,
			playerA,
			reverse_player,
			forward_player,
			playerArrayFields
		);

		if (!isEmptyObject(reverse_player) || !isEmptyObject(forward_player)) {
			reverse.playerStateArray.push({
				id: playerB.id,
				...reverse_player,
			});
			forward.playerStateArray.push({
				id: playerA.id,
				...forward_player,
			});
		}
	}

	return { forward, reverse };
}

export function applyPatchCustom(state: GameState, patch: any): GameState {
	const newState: any = JSON.parse(JSON.stringify(state)); // Deep clone to avoid mutation

	function applySimpleFields(target: any, source: any, fields: string[]) {
		for (const field of fields) {
			if (field in source) {
				target[field] = source[field];
			}
		}
	}

	function applyArrayFields(target: any, source: any, fields: string[]) {
		for (const field of fields) {
			if (field in source) {
				target[field] = Array.isArray(source[field])
					? JSON.parse(JSON.stringify(source[field]))
					: source[field];
			}
		}
	}

	// Apply nested object like worldEnergy
	if ('worldEnergy' in patch) {
		newState.worldEnergy = JSON.parse(JSON.stringify(patch.worldEnergy));
	}

	// Apply simple and array fields
	applySimpleFields(newState, patch, gameStateSimpleFields);
	applyArrayFields(newState, patch, gameStateArrayFields);

	// Apply playerStateArray patches
	if ('playerStateArray' in patch) {
		for (const playerPatch of patch.playerStateArray) {
			const index = newState.playerStateArray.findIndex(
				(p: any) => p.id === playerPatch.id
			);

			if (index !== -1) {
				const player = newState.playerStateArray[index];

				applySimpleFields(player, playerPatch, playerSimpleFields);
				applyArrayFields(player, playerPatch, playerArrayFields);
			} else {
				// Player not found, optionally handle this case (e.g. add player, skip, or throw)
				console.warn(
					`Player with ID ${playerPatch.id} not found in state.`
				);
			}
		}
	}

	return newState;
}

export function areObjectsIdentical(b: any, a: any): boolean {
	const debug = false;
	function objectEqualityDebugger(
		msg: string,
		field: string,
		objA: any = {},
		objB: any = {}
	) {
		if (!debug) return;

		console.log(msg, field);
		console.log(objA);
		console.log(objB);
	}

	for (const field of gameStateSimpleFields) {
		if (b[field] !== a[field]) {
			objectEqualityDebugger(
				'gameStateSimpleFields mismatch',
				field,
				a,
				b
			);
			return false;
		}
	}

	for (const field of gameStateArrayFields) {
		if (!arraysAreEqual(b[field], a[field])) {
			objectEqualityDebugger(
				'gameStateArrayFields mismatch',
				field,
				a,
				b
			);
			return false;
		}
	}

	// Compare worldEnergy (if it exists)
	if (JSON.stringify(b.worldEnergy) !== JSON.stringify(a.worldEnergy)) {
		objectEqualityDebugger(
			'worldEnergy mismatch',
			'',
			a.worldEnergy,
			b.worldEnergy
		);
		return false;
	}

	// Compare playerStateArray (if it exists)
	if (b.playerStateArray.length !== a.playerStateArray.length) {
		objectEqualityDebugger('playerStateArray length mismatch', '');
		return false;
	}

	for (let i = 0; i < b.playerStateArray.length; i++) {
		const playerB = b.playerStateArray[i];
		const playerA = getMatchingPlayerById(playerB.id, a);

		if (!playerA) {
			objectEqualityDebugger(
				"Player not found in 'a'",
				'',
				playerA,
				playerB
			);
			return false; //
		}

		for (const field of playerSimpleFields) {
			if (playerB[field] !== playerA[field]) {
				objectEqualityDebugger(
					'playerSimpleFields not equal',
					field,
					playerA[field],
					playerB[field]
				);
				return false;
			}
		}

		for (const field of playerArrayFields) {
			if (!arraysAreEqual(playerB[field], playerA[field])) {
				objectEqualityDebugger(
					'playerArrayFields not equal',
					field,
					playerA[field],
					playerB[field]
				);
				return false;
			}
		}
	}

	return true; // If no differences were found, objects are identical
}

// Helper function to compare arrays
function arraysAreEqual(a1: any[], a2: any[]): boolean {
	if (a1.length !== a2.length) return false;

	for (let i = 0; i < a1.length; i++) {
		if (Array.isArray(a1[i]) && Array.isArray(a2[i])) {
			if (!arraysAreEqual(a1[i], a2[i])) return false;
		} else if (a1[i] !== a2[i]) {
			return false;
		}
	}
	return true;
}
